import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../../lib/utils';
import { cardClass, primaryText, semiBoldText } from '../../../../../styling/stylingHelper';
import { TableComponent } from '../../../../ui/TableComponent';
import {
  ResponsiveTable,
  MobileCardList,
  MobileCardField,
} from '../../../../shared/mobile/ResponsiveTable';
import {
  HydratedTableBlock,
  TableCell,
  TableColumn,
} from '../../../../../services/admin/cloudAgentTypes';
import { BlockSourceNote } from './BlockSourceNote';
import { alignForColumn, formatCell, formatList } from './tableColumnFormat';

type Row = Record<string, TableCell>;

/** Rows shown per "Load more" step in the mobile card list. */
const MOBILE_PAGE_SIZE = 10;

/**
 * Column types that render through TableComponent's own `renderByType` instead of a formatted
 * string, so the agent's tables reuse the app-wide pills rather than inventing a second look.
 *
 * Only `category`. `quantity` deliberately does NOT map to `blueQuantity`: that pill is the
 * app's affordance for an EDITABLE quantity (order history opens an editor on click), which
 * would be a lie in a read-only agent report — and being a pill it cannot right-align, so it
 * would sit left under a right-aligned header. Quantities render as plain right-aligned numbers.
 */
const NATIVE_COLUMN_TYPES: Record<string, 'category'> = {
  category: 'category',
};

/** One formatted desktop cell, aligned per column type and carrying a title when truncated. */
const Cell: React.FC<{ value: TableCell; column: TableColumn }> = ({ value, column }) => {
  const align = alignForColumn(column.type, column.align);
  const title = column.type === 'list' ? formatList(value).title : undefined;
  return (
    <span className={cn('block', align === 'right' ? 'text-right' : 'text-left')} title={title}>
      {formatCell(value, column.type)}
    </span>
  );
};

/**
 * table → TableComponent on desktop, a stacked card list below `sm`. Both views format through
 * the same `formatCell`, so a number never reads differently between them.
 *
 * TableComponent centres every non-first column by default, which left text columns floating in
 * the middle and headers misaligned from their right-aligned values. `leftAlignCells` makes the
 * cell default left, and each column then asserts its own alignment on BOTH the header label and
 * the value — so alignment is driven by the column's type rather than its position.
 */
export const TableBlock: React.FC<{ block: HydratedTableBlock }> = ({ block }) => {
  const { t } = useTranslation();
  const [mobileCount, setMobileCount] = useState(MOBILE_PAGE_SIZE);

  const title = block.title ?? t('cloudAgent.blockTitles.table');
  const columns = block.data.columns.map((col) => {
    const align = alignForColumn(col.type, col.align);
    const native = col.type ? NATIVE_COLUMN_TYPES[col.type] : undefined;
    return {
      key: col.key,
      // Headers carry the same alignment as their values, or they read as a different column.
      label: (
        <span className={cn('block', align === 'right' ? 'text-right' : 'text-left')}>
          {col.label}
        </span>
      ),
      // `category` defers to the shared pill renderer; everything else is a formatted string.
      ...(native
        ? { columnType: native }
        : { render: (value: TableCell) => <Cell value={value} column={col} /> }),
    };
  });

  const [headCol, ...restCols] = block.data.columns;
  const rows = block.data.rows;

  return (
    <div>
      <ResponsiveTable
        desktop={
          <TableComponent<Row>
            title={title}
            data={rows}
            columns={columns}
            blankLines={false}
            options={{ leftAlignCells: true }}
          />
        }
        mobile={
          <div className="flex flex-col gap-3">
            {/* break-words so a long single-word title cannot widen the card past the screen. */}
            <p className={cn(primaryText, 'text-sm break-words', semiBoldText)}>{title}</p>
            <MobileCardList
              items={rows.slice(0, mobileCount)}
              keyOf={(_row, i) => i}
              onLoadMore={() => setMobileCount((c) => c + MOBILE_PAGE_SIZE)}
              hasMore={mobileCount < rows.length}
              loadMoreLabel={t('cloudAgent.table.loadMore')}
              renderCard={(row) => (
                <div className={cn(cardClass, 'p-4 flex flex-col gap-3')}>
                  {headCol && (
                    <MobileCardField
                      label={headCol.label}
                      value={formatCell(row[headCol.key], headCol.type)}
                    />
                  )}
                  {restCols.length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      {restCols.map((col) => (
                        <MobileCardField
                          key={col.key}
                          label={col.label}
                          value={formatCell(row[col.key], col.type)}
                          align={alignForColumn(col.type, col.align)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            />
          </div>
        }
      />
      <BlockSourceNote source={block.source} />
    </div>
  );
};
